import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Lock } from "lucide-react";

const BASE_TIERS = [
  {
    id: "founding",
    eyebrow: "Founding 45",
    title: "First cup, on us",
    price: "Free",
    note: "For the first 45 founding members",
    features: [
      "Your first bowl (Shizuoka grade) free",
      "Priority on opening week",
      "First access to single-origin drops",
      "Counts toward the founding 100",
    ],
    accent: true,
    cta: "Claim my spot",
    action: "join",
  },
  {
    id: "five",
    eyebrow: "The Five (founding pass)",
    title: "5-bowl pass",
    price: "10% off",
    inr: 1710,
    note: "For a slow month",
    features: [
      "Any five Shizuoka-grade drinks",
      "Valid 90 days from purchase",
      "Use across hot, iced or fusion",
      "Locks in your founding-100 seat",
    ],
    cta: "Pay ₹1,710",
    action: "buy",
    tier: "five",
  },
  {
    id: "ten",
    eyebrow: "The Ten (founding pass)",
    title: "10-bowl pass",
    price: "15% off",
    inr: 3230,
    note: "For a daily-ish ritual",
    features: [
      "Any ten Shizuoka-grade drinks",
      "Skip-the-queue privilege",
      "Bring-a-friend Sundays",
      "Locks in your founding-100 seat",
    ],
    cta: "Pay ₹3,230",
    action: "buy",
    tier: "ten",
  },
];

export default function MatchaPass({ onJoin, onBuyPass, stats }) {
  const foundersClosed = !!stats?.founders_closed;
  const passesClosed = !!stats?.passes_closed;
  const foundersTotal = stats?.founders_total ?? 45;
  const passesTotal = stats?.passes_total ?? 55;
  const totalCap = stats?.total_cap ?? 100;

  return (
    <section id="pass" className="px-6 md:px-10 py-24 md:py-32 bg-[#F1EEE6] border-y border-border" data-testid="matcha-pass-section">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <p className="overline text-[#C47253] mb-4">The Matcha Pass</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-[1.05] text-[#1A2B22]">
              Pay <em className="text-[#2C4C3B]">honestly</em>. Return often.
            </h2>
          </div>
          <p className="text-[#5C7365] max-w-sm text-sm leading-relaxed">
            {totalCap} founding members in total. The first {foundersTotal} receive a free cup; the remaining {passesTotal} join via a Matcha Pass.
            Passes apply to the Shizuoka grade only — Uji specialty stays full-price always.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {BASE_TIERS.map((t) => {
            const closed = t.action === "join" ? foundersClosed : passesClosed;
            const remaining =
              t.action === "join"
                ? stats?.founders_remaining
                : stats?.passes_remaining;
            const eyebrow = t.action === "join" ? `Founding ${foundersTotal}` : t.eyebrow;
            const note = t.action === "join" ? `For the first ${foundersTotal} founding members` : t.note;

            return (
              <div
                key={t.id}
                data-testid={`pass-card-${t.id}`}
                className={[
                  "rounded-3xl p-8 md:p-10 flex flex-col border transition-all duration-500",
                  t.accent
                    ? "bg-[#2C4C3B] text-[#F7F5F0] border-[#2C4C3B] md:scale-[1.02]"
                    : "bg-background border-border hover:border-[#2C4C3B]/40",
                  closed ? "opacity-70" : "hover:-translate-y-1",
                ].join(" ")}
              >
                <p className={"overline mb-6 " + (t.accent ? "text-[#EBEFDE]" : "text-[#C47253]")}>
                  {eyebrow}
                </p>
                <h3 className="font-serif text-3xl md:text-4xl leading-none">{t.title}</h3>
                <div className={"mt-3 text-2xl font-serif italic " + (t.accent ? "text-[#EBEFDE]" : "text-[#2C4C3B]")}>
                  {t.price}
                  {t.inr && (
                    <span className={"ml-3 text-sm not-italic font-sans " + (t.accent ? "text-[#DDE3D5]" : "text-[#5C7365]")}>
                      ₹{t.inr.toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
                <p className={"mt-2 text-sm " + (t.accent ? "text-[#DDE3D5]" : "text-[#5C7365]")}>{note}</p>
                {typeof remaining === "number" && stats?.loaded && (
                  <p
                    className={
                      "mt-2 text-xs font-medium tracking-wide " +
                      (t.accent ? "text-[#EBEFDE]" : "text-[#2C4C3B]")
                    }
                    data-testid={`pass-card-${t.id}-remaining`}
                  >
                    {closed
                      ? "Filled — thank you"
                      : `${remaining} of ${t.action === "join" ? stats.founders_total : stats.passes_total} left`}
                  </p>
                )}

                <ul className="mt-7 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm leading-relaxed">
                      <Check className={"w-4 h-4 mt-0.5 shrink-0 " + (t.accent ? "text-[#EBEFDE]" : "text-[#2C4C3B]")} />
                      <span className={t.accent ? "text-[#F7F5F0]/95" : "text-[#1A2B22]/85"}>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => (t.action === "buy" ? onBuyPass(t.tier) : onJoin())}
                  disabled={closed}
                  data-testid={`pass-cta-${t.id}`}
                  className={[
                    "mt-9 rounded-full h-12 text-xs tracking-[0.2em] uppercase group disabled:opacity-50 disabled:cursor-not-allowed",
                    t.accent
                      ? "bg-[#F7F5F0] text-[#2C4C3B] hover:bg-[#EBEFDE]"
                      : "bg-primary text-primary-foreground hover:bg-[#1F382B]",
                  ].join(" ")}
                >
                  {closed ? (
                    <><Lock className="w-3.5 h-3.5 mr-2" /> {t.action === "join" ? "All free cups claimed" : "Sold out"}</>
                  ) : (
                    <>{t.cta} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" /></>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
